import { toNavGroupName } from '@src/hooks/useSpacesNavItems';
import type { LayoutSection, LayoutSpec } from '../../../types/LayoutConfig';
import { parseLayoutIdFromSectionPath } from '../../NavigationSidebar/spacesNavPath';
import { isUserLayoutId } from './userLayoutConfig';

export interface SpaceSectionLocation {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * How many path segments after `/:section` belong to the space root before deep links:
 * `1` for `/section/:layoutId`, `2` for `/section/:navGroupName/:layoutId`.
 * Prefer known group slugs, then fall back to known layout ids so stale group slugs are
 * still interpreted as grouped when the second segment is a valid layout id.
 */
export function getSpacePrefixCount(
  pathname: string,
  section: LayoutSection,
  navGroupNames: readonly string[],
  knownLayoutIds: ReadonlySet<string>
): 1 | 2 {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== section) {
    return 1;
  }
  const seg1 = parts[1];
  if (seg1 != null && navGroupNames.includes(seg1)) {
    return 2;
  }

  // Handle stale/unknown group slugs by detecting grouped shape from known layout ids.
  const parsedLayoutId = parseLayoutIdFromSectionPath(pathname, section, knownLayoutIds);
  return parsedLayoutId != null && parsedLayoutId === parts[2] ? 2 : 1;
}

/**
 * Redirect target for unknown/missing layouts. Returns `null` when the URL is already canonical.
 */
export function getSpaceRedirectLocation({
  location,
  section,
  indexRedirectPath,
  navGroupNames,
  knownLayoutIds,
}: {
  location: SpaceSectionLocation;
  section: LayoutSection;
  indexRedirectPath: string;
  navGroupNames: readonly string[];
  knownLayoutIds: ReadonlySet<string>;
}): string | undefined {
  const { pathname } = location;
  const prefixCount = getSpacePrefixCount(pathname, section, navGroupNames, knownLayoutIds);
  return redirectAfterSpacePrefixes(indexRedirectPath, pathname, section, prefixCount, location);
}

/**
 * Path suffix after `/section` and the next `prefixSegmentCount` segments
 * (one segment for ungrouped `/section/:layoutId`, two for `/section/:group/:layoutId`).
 */
export function pathAfterSpacePrefixes(pathname: string, section: LayoutSection, prefixSegmentCount: number): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== section) {
    return '';
  }
  const rest = parts.slice(1 + prefixSegmentCount);
  return rest.length > 0 ? `/${rest.join('/')}` : '';
}

/**
 * When the URL should be rewritten for space routing, returns the full location string
 * (`pathname` + `search` + `hash`); otherwise `null`.
 *
 * - Unknown `layoutId` → last-used space base (`indexRedirectPath`) plus the deep-link tail
 *   after the current space prefix (1 or 2 segments, matching the matched route).
 * - Known layout in a group, but matched the ungrouped route → insert `/:navGroupSlug/:layoutId`.
 * - Known layout without a group but matched a grouped route → drop the extra segment.
 * - Wrong group slug for a grouped layout → canonical `/:section/:slug/:layoutId` plus tail.
 */
export function getSpacesRedirectLocation({
  section,
  location,
  layoutId,
  routeNavGroupSlug,
  layoutConfigsMap,
  indexRedirectPath,
}: {
  section: LayoutSection;
  location: SpaceSectionLocation;
  layoutId: string | undefined;
  /** First URL segment after `/:section` when the grouped route matched; else undefined. */
  routeNavGroupSlug: string | undefined;
  layoutConfigsMap: Record<string, LayoutSpec> | undefined;
  indexRedirectPath: string;
}): string | undefined {
  const { pathname } = location;
  // Which route shape matched: `/:section/:layoutId/...` vs `/:section/:groupSlug/:layoutId/...`
  const groupedRoute = routeNavGroupSlug != null && routeNavGroupSlug !== '';

  if (layoutId == null) {
    return undefined;
  }

  // Shared URL or stale layout id: keep deep-link tail but anchor under the user’s current default space.
  if (!isUserLayoutId(layoutId, layoutConfigsMap)) {
    if (groupedRoute) {
      return redirectAfterSpacePrefixes(indexRedirectPath, pathname, section, 2, location);
    }

    const { knownLayoutIds, navGroupNames } = getKnownSpaceRoutingMetadata(layoutConfigsMap);
    return getSpaceRedirectLocation({
      location,
      section,
      indexRedirectPath,
      navGroupNames,
      knownLayoutIds,
    });
  }

  const groupLabel = layoutConfigsMap?.[layoutId]?.group;
  const expectedSlug = groupLabel != null && groupLabel !== '' ? toNavGroupName(groupLabel) : null;

  if (expectedSlug != null) {
    const canonicalBase = `/${section}/${expectedSlug}/${layoutId}`;
    // Grouped layout but URL hit the ungrouped route — insert group slug before layout id.
    if (!groupedRoute) {
      return redirectAfterSpacePrefixes(canonicalBase, pathname, section, 1, location);
    }
    // Grouped layout on grouped route but slug does not match config — rewrite to canonical slug.
    if (routeNavGroupSlug !== expectedSlug) {
      return redirectAfterSpacePrefixes(canonicalBase, pathname, section, 2, location);
    }
    return undefined;
  }

  const canonicalBase = `/${section}/${layoutId}`;
  // Ungrouped layout but URL has an extra group segment — drop it.
  if (groupedRoute) {
    return redirectAfterSpacePrefixes(canonicalBase, pathname, section, 2, location);
  }
  // Ungrouped route: normalize rare cases where pathname is not under the expected base.
  if (pathname !== canonicalBase && !pathname.startsWith(`${canonicalBase}/`)) {
    return redirectAfterSpacePrefixes(canonicalBase, pathname, section, 1, location);
  }
  return undefined;
}

function targetIfChanged(base: string, rest: string, location: SpaceSectionLocation): string | undefined {
  const target = `${base}${rest}${location.search}${location.hash}`;
  const current = `${location.pathname}${location.search}${location.hash}`;
  return target !== current ? target : undefined;
}

function redirectAfterSpacePrefixes(
  base: string,
  pathname: string,
  section: LayoutSection,
  prefixSegmentCount: number,
  location: SpaceSectionLocation
): string | undefined {
  const rest = pathAfterSpacePrefixes(pathname, section, prefixSegmentCount);
  return targetIfChanged(base, rest, location);
}

function getKnownSpaceRoutingMetadata(layoutConfigsMap: Record<string, LayoutSpec> | undefined): {
  knownLayoutIds: Set<string>;
  navGroupNames: string[];
} {
  const knownLayoutIds = new Set(Object.keys(layoutConfigsMap ?? {}));
  const navGroupNames = new Set<string>();
  for (const layout of Object.values(layoutConfigsMap ?? {})) {
    const groupLabel = layout.group;
    if (groupLabel != null && groupLabel !== '') {
      navGroupNames.add(toNavGroupName(groupLabel));
    }
  }
  return { knownLayoutIds, navGroupNames: [...navGroupNames] };
}
